import { Box, List, ListItem, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AxiosResponse } from 'axios';
import { Buffer } from 'buffer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router-dom";
import { BusinessUnitDetails, LanguageCulture, PaymentSolutionNameRequest, ValidationRequest, ValidationResponse, getBusinessUnitDetails, getValidation } from '../../Api/BoardingApi';
import { useLanguage } from '../../LanguageContext';
import i18n from '../../i18n';
import Footer from '../Footer/Footer';
import GetStartedForm from '../GetStartedForm/GetStartedForm';
import Header from '../Header/Header';
import Loader from '../Loader/Loader';
import PageContent from '../PageContent/PageContent';
import Sidebar from '../Sidebar/Sidebar';

const Landing: any = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [businessUnitDetails, setBusinessUnitDetails] = useState<BusinessUnitDetails>({
        businessUnitName: "",
        fullsteamEmail: "",
        logoArray: "",
        languageCultures: []
    });
    const { paymentSolutionName } = useParams<{ paymentSolutionName: string }>();
    const { culture } = useParams<{ culture: string }>();
    const { base64String } = useParams<{ base64String: string }>();
    const [businessUnitCustomerId, setBusinessUnitCustomerId] = useState<string | null>(null);
    const [merchantApplicationIdentifier, setMerchantApplicationIdentifier] = useState<string | null>(null);
    const [availableLanguageCultures, setAvailableLanguageCultures] = useState<LanguageCulture[] | null>(null);
    const [isDecoded, setIsDecoded] = useState<boolean>(false);
    const navigate = useNavigate();

    //Handle translation
    const { changeLanguage } = useLanguage();
    const { t } = useTranslation();

    useEffect(() => {
        if (culture) {
            const supportedLngs = i18n.options.supportedLngs;
            if (supportedLngs && supportedLngs.includes(culture)) {
                changeLanguage(culture);
            }
            else if (!base64String) {
                // check if base64String was included without culture info
                decodeBase64String(culture);
            }
        }
        else {
            navigate(`/${paymentSolutionName}`);
        }
    }, [culture]);

    const finishLoading = (elapsedMilliseconds: number) => {
        let waitTime = normalizedWaitMilliseconds(elapsedMilliseconds);
        setTimeout(() => { setIsLoading((isLoading: boolean) => !isLoading) }, waitTime);
    }

    const normalizedWaitMilliseconds = (elapsedMilliseconds: number) => {
        if (elapsedMilliseconds < 0)
            return 0;

        let bufferMilliseconds = 300;
        let normalizedWaitMilliseconds = bufferMilliseconds - elapsedMilliseconds;
        return Math.max(normalizedWaitMilliseconds, 0);
    }

    const hasInvalidCharacters = (str: string) => {
        // Match any non-printable character (e.g., ASCII control characters, extended chars, etc.)
        const badCharPattern = /[^\x20-\x7E\u00A0-\uD7FF]/; // Includes ASCII printable range and most Unicode characters
        return badCharPattern.test(str);
    };

    const decodeBase64String = (base64String: string | null) => {
        setIsDecoded(true);

        if (!base64String) return;

        const buffer = Buffer.from(base64String, 'base64');
        const decodedBase64String = buffer.toString('utf-8');

        if (hasInvalidCharacters(decodedBase64String)) {
            console.log('Could not parse url.');
            navigate('/notFound');
            return;
        }

        const params = new URLSearchParams(decodedBase64String);
        const businessUnitCustomerIdParam = params.get('BusinessUnitCustomerId');
        const merchantApplicationIdentifierParam = params.get('MerchantApplicationIdentifier');

        if (businessUnitCustomerIdParam)
            setBusinessUnitCustomerId(businessUnitCustomerIdParam);
        if (merchantApplicationIdentifierParam)
            setMerchantApplicationIdentifier(merchantApplicationIdentifierParam);
    }

    useEffect(() => {
        let isMounted = true;

        const fetchValidation = async () => {

            // Validate businessUnitCustomerId length
            if (businessUnitCustomerId && businessUnitCustomerId.length > 100) {
                console.log(`businessUnitCustomerId length must not exceed 100.`);
                if (isMounted) {
                    navigate('/error');
                    return;
                }
            }

            // Validate merchantApplicationIdentifier is Guid
            if (merchantApplicationIdentifier) {
                const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                if (!regex.test(merchantApplicationIdentifier)) {
                    console.log(`merchantApplicationIdentifier must be a valid guid.`);
                    if (isMounted) {
                        navigate('/error');
                        return;
                    }
                }
            }

            const request: ValidationRequest = {
                businessUnitCustomerId: businessUnitCustomerId,
                merchantApplicationIdentifier: merchantApplicationIdentifier
            };

            // Server-side validation
            try {
                const response: AxiosResponse<ValidationResponse> = await getValidation(request);
                if (!response.data?.isValid && isMounted) {
                    console.log(`businessUnitCustomerId: '${businessUnitCustomerId}' or merchantApplicationIdentifier: '${merchantApplicationIdentifier}' failed validation.`);
                    navigate(`/notfound`);
                    return;
                }
            } catch (error) {
                console.error('Error fetching validation:', error);
                if (isMounted) {
                    navigate('/error');
                    return;
                }
            }
        };

        if (businessUnitCustomerId || merchantApplicationIdentifier) {

            fetchValidation();

            // businessUnitCustomerId and merchantApplicationIdentifier were set by culture becuase an actual culture was not supplied
            // navigate to region select and persist base64String (*using culture)
            if (culture && !base64String && isMounted) {
                navigate(`/${paymentSolutionName}/_/${culture}`);
                return;
            }

            // businessUnitCustomerId and merchantApplicationIdentifier were set but culture is not valid
            // navigate to region select and persist base64String
            if (!culture || (availableLanguageCultures && !availableLanguageCultures.some(lc => lc.culture === culture)) && isMounted) {
                navigate(`/${paymentSolutionName}/_/${base64String}`);
                return;
            }
        }
        else if (isDecoded && isMounted && !businessUnitCustomerId && !merchantApplicationIdentifier && culture) {

            const supportedLngs = i18n.options.supportedLngs;
            if (supportedLngs && !supportedLngs.includes(culture)) {
                navigate(`/${paymentSolutionName}`);
                return;
            }
        }

        return () => {
            isMounted = false;
        };
    }, [businessUnitCustomerId, merchantApplicationIdentifier, paymentSolutionName, base64String, availableLanguageCultures, navigate]);

    useEffect(() => {
        let isMounted = true;

        const fetchBusinessUnitDetails = async () => {
            if (!paymentSolutionName) return;

            const startTime = Date.now();

            const paymentSolutionNameRequest: PaymentSolutionNameRequest = {
                paymentSolutionName
            };

            try {
                const response: AxiosResponse<BusinessUnitDetails> = await getBusinessUnitDetails(paymentSolutionNameRequest);
                if (isMounted) {
                    setBusinessUnitDetails(response.data);
                    setAvailableLanguageCultures(response.data.languageCultures);
                }
            } catch (error) {
                console.error('Error fetching business unit details:', error);
                if (isMounted) {
                    navigate('/error');
                    return;
                }
            } finally {
                if (isMounted) {
                    const elapsedMilliseconds = Date.now() - startTime;
                    finishLoading(elapsedMilliseconds);
                }
            }
        };

        const decodeBase64StringAsync = async () => {
            if (!base64String) return;

            decodeBase64String(base64String);
        };

        decodeBase64StringAsync();
        fetchBusinessUnitDetails();

        return () => {
            isMounted = false;
        };
    }, [paymentSolutionName, culture, base64String, navigate]);

    document.title = t('Document_title').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const heading = t('Page_heading').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const sideBarHeading: string = t('Landing_sideBarHeading');
    const sideBarText: string = t('Landing_sideBarText');
    const sideBarBullet1: string = t('Landing_sideBarBullet1');
    const sideBarBullet2: string = t('Landing_sideBarBullet2');
    const sideBarBullet3: string = t('Landing_sideBarBullet3');
    const sideBarBullet4: string = t('Landing_sideBarBullet4');
    const sideBarBullet5: string = t('Landing_sideBarBullet5');
    const sideBarBullet6: string = t('Landing_sideBarBullet6');
    const sideBarBullet7: string = t('Landing_sideBarBullet7');
    const sideBarBullet8: string = t('Landing_sideBarBullet8');
    const pageContentHeading: string = t('Landing_pageContentHeading');

    return (

        <> {isLoading ?
            <Loader /> :
            <Box id="landing">
                <Box className="banner" />
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>

                    <Grid size={{ xs: 4, sm: 8, md: 4 }}>
                        <Sidebar title={sideBarHeading} logo={businessUnitDetails?.logoArray} alt={businessUnitDetails?.businessUnitName}>
                            <Typography variant="body2" data-test-id="form-sidebar-top-text" component="p" gutterBottom>{sideBarText}</Typography>
                            <List>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-1" component="span" gutterBottom>&#8226; {sideBarBullet1}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-2" component="span" gutterBottom>&#8226; {sideBarBullet2}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-3" component="span" gutterBottom>&#8226; {sideBarBullet3}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-4" component="span" gutterBottom>&#8226; {sideBarBullet4}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-5" component="span" gutterBottom>&#8226; {sideBarBullet5}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-6" component="span" gutterBottom>&#8226; {sideBarBullet6}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-7" component="span" gutterBottom>&#8226; {sideBarBullet7}</Typography></ListItem>
                                <ListItem><Typography variant="body2" data-test-id="form-sidebar-bullet-8" component="span" gutterBottom>&#8226; {sideBarBullet8}</Typography></ListItem>
                            </List>
                        </Sidebar>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 8, md: 8 }}>
                        <Header><span id="landing-business-unit-heading-text">{heading}</span></Header>
                        <PageContent title={pageContentHeading}>
                            <GetStartedForm
                                paymentSolutionName={businessUnitDetails?.businessUnitName}
                                culture={culture}
                                businessUnitCustomerId={businessUnitCustomerId}
                                merchantApplicationIdentifier={merchantApplicationIdentifier} />
                        </PageContent>
                        <Footer culture={culture} />
                    </Grid>

                </Grid>
            </Box>
        }
        </>
    );
}

export default Landing;